import { usePageHeader } from "contexts/PageHeaderContext";
import { useState, useEffect, useCallback, useRef } from "react";
import {
  Save, HandCoins, DollarSign, Calendar, FileText, Upload, UserPlus, Shield, Trash2, CheckCircle2,
  AlertCircle, Badge, Eye, History, ChevronUp, ChevronDown, Loader2, CircleX,
  Wallet
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Select, SelectContent, SelectItem, SelectValue } from "../../ui/select";
import { useNavigate, useParams } from "react-router-dom";
import { InputFieldset } from "ui/inputFieldset";
import { SelectFieldset } from "ui/selectFieldset";
import { SelectTriggerUI } from "ui/selectTrigger";
import { TextareaUI } from "ui/textareaUI";
import { Input } from "../../ui/input";
import { getQarzanApplicationById, getQarzanListTypes, reUploadQarzanApplicantDocuments, updateQarzanApplicant } from "services/qarzanApplicationService";
import { FullScreenLoading } from "components/ui/loader";
import { DocumentHistoryNew, EmployeeInfo, GuarantorApiResponse, GuarantorError, QarzanDocumentUpload, QarzanFormErrors, QarzanTypesApi } from "types/qarzanHassana";
import { Alert, AlertDescription } from "ui/alert";
import { getScreenDocuments } from "services/documentService";
import { DocumentApiResponse, rejectTourOperatorDocument } from "services/newRegistrationService";
import { allowOnlyNumbers, formatFileSizeFromKB, formatUploadedDateTime, getFileViewUrl, removeSpecialCharacters, validatePhoneNumberInput } from "utils/commonutils";
import axios from "axios";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Label } from "ui/label";
import { Textarea } from "ui/textarea";
// import { useAuth } from '../../contexts/AuthContext';

interface QarzanHasanaFormPageProps {
  onNavigate: (page: string) => void;
}

interface QarzanApiDocument {
  documentId?: number;
  documentMasterId?: number;
  documentName?: string;
  status?: string;
  isMandatory?: boolean;
  fileKey?: string;
  originalFileName?: string;
  filePath?: string;
  acceptedFormats?: string;
  maxFileSizeKB?: string;
  versions?: DocumentHistoryNew[];
  histories?: [];
  moduleCode?: string;
}

export const QarzanHasanaFormEditPage = ({
  onNavigate,
}: QarzanHasanaFormPageProps) => {

  // const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const { setPageHeader } = usePageHeader();
  useEffect(() => {
    setPageHeader({
      title: "Edit Application",
      childTitle: "Submit interest-free loan application",
      icon: <Wallet className="h-5 w-5" />,
      onBack: () => navigate(-1),
    });
    return () => setPageHeader(null);
  }, [setPageHeader, navigate]);
  const [loading, setLoading] = useState(false);
  const [refetchApi, setRefetchApi] = useState(false);

  // PERSONAL & APPLICATION DETAILS
  const [qarzanTypes, setQarzanTypes] = useState<QarzanTypesApi[]>([]);
  const [employeeData, setEmployeeData] = useState<EmployeeInfo>({
    employeeId: "",
    name: "",
    itsNumber: "",
    department: "",
    departmentNames: "",
    salaryCurrency: "",
    CurrencyCode: "",
    // CurrencyId: "",
  });
  const [applicationNo, setApplicationNo] = useState(``);
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [qarzanAmount, setQarzanAmount] = useState<number>(0);
  const [qarzanType, setQarzanType] = useState("");
  const [installments, setInstallments] = useState<number>(0);
  const [repaymentStartDate, setRepaymentStartDate] = useState("");
  const [againstSalary, setAgainstSalary] = useState("complete");
  const [partialAmount, setPartialAmount] = useState<number>(0);
  const [remark, setRemark] = useState("");

  // Rehen (Gold Security) Details
  const [hasRehen, setHasRehen] = useState(false);
  const [rehenGoldWeight, setRehenGoldWeight] = useState<number>(0);
  const [rehenGoldKarat, setRehenGoldKarat] = useState("");
  const [rehenEstimatedValue, setRehenEstimatedValue] = useState<number>(0);
  const [rehenLocation, setRehenLocation] = useState("");
  const [rehenCustodian, setRehenCustodian] = useState("");
  const [rehenCustodianContact, setRehenCustodianContact] = useState("");
  const [rehenDescription, setRehenDescription] = useState("");

  // Document UPLOAD
  const [expandedDocHistory, setExpandedDocHistory] = useState<
    Record<string, boolean>
  >({});
  const [isUploading, setIsUploading] = useState(false);
  const [documents, setDocuments] = useState<QarzanDocumentUpload[]>([]);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<string | null>(null);
  const [deleteRemark, setDeleteRemark] = useState("");
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [rejectedDoc, setRejectedDoc] = useState<QarzanDocumentUpload | null>(null);
  const [reUploadFiles, setReuploadFiles] = useState<File[]>([]);

  // Guarantor Details
  const [guarantors, setGuarantors] = useState([
    {
      guarantorName: "",
      guarantorItsNumber: "",
      guarantorRelation: "",
      guarantorPhone: "",
      guarantorEmail: "",
      guarantorAddress: "",
    },
  ]);

  const addGuarantor = () => {
    setGuarantors([
      ...guarantors,
      {
        guarantorName: "",
        guarantorItsNumber: "",
        guarantorRelation: "",
        guarantorPhone: "",
        guarantorEmail: "",
        guarantorAddress: "",
      },
    ]);
  };

  const toggleDocHistory = (docId: string) => {
    setExpandedDocHistory((prev) => ({
      ...prev,
      [docId]: !prev[docId],
    }));
  };

  const removeGuarantor = (index: number): void => {
    const updatedGuarantors = guarantors.filter((_, i) => i !== index);
    setGuarantors(updatedGuarantors);
    setFormErrors((prev) => ({
      ...prev,
      guarantors: prev.guarantors?.filter((_, i) => i !== index) || [],
    }));
  };

  const handleGuarantorChange = (
    index: number,
    // field: keyof Guarantor,
    field: string,
    value: string,
  ): void => {
    const updatedGuarantors = [...guarantors];

    updatedGuarantors[index] = {
      ...updatedGuarantors[index],
      [field]: value,
    };

    setGuarantors(updatedGuarantors);

    setFormErrors((prev) => ({
      ...prev,
      guarantors:
        prev.guarantors?.map((error, i) =>
          i === index
            ? {
              ...error,
              [field]: undefined,
            }
            : error,
        ) || [],
    }));
  };

  const [formErrors, setFormErrors] = useState<QarzanFormErrors>({
    // date: "",
    qarzanType: "",
    qarzanAmount: "",
    installments: "",
    repaymentStartDate: "",
    againstSalary: "",
    partialAmount: "",
    rehenGoldWeight: "",
    rehenGoldKarat: "",
    rehenLocation: "",
    rehenCustodian: "",
    document: "",
  });

  const fetchDocuments = useCallback(
    async (screenName: string): Promise<QarzanDocumentUpload[]> => {
      const response = await getScreenDocuments(screenName);
      if (!response) return [];

      return response.map((doc: DocumentApiResponse) => ({
        id: String(doc.documentId),
        name: doc.documentName,
        file: null,
        isRequired: doc.isMandatory,
        acceptedFormatsList: doc?.acceptedFormatsList,
        // acceptedFormats: doc?.acceptedFormatsList,
        maxFileSizeKB: doc?.maxFileSizeKB,
      }));
    },
    [],
  );

  const getQarzanTypes = useCallback(async () => {
    try {
      const data = await getQarzanListTypes();
      setQarzanTypes(data);
    } catch {
      toast.error("Failed to fetch Employee Details");
    } finally {
    }
  }, []);

  const getQarzanDetails = useCallback(async (masterDocs?: QarzanDocumentUpload[]) => {
    try {
      // setLoading(true);
      const result = await getQarzanApplicationById(String(id));
      // Basic Fields
      const data = result?.application;
      const docResult = result?.documents;
      setApplicationNo(data.applicationNumber);
      setDate(data.applicationDate?.split("T")[0]);
      setEmployeeData({
        employeeId: data.employeeId || "",
        name: data.employeeName || "",
        itsNumber: data.itsNumber || "",
        department: data?.departmentIds?.join(", ") || "",
        departmentNames: data?.departmentNames?.join(", ") || "",
        salaryCurrency: data?.currencyId || "",
        CurrencyCode: data?.currencyCode || "",
      });

      setQarzanType(String(data.qarzanTypeId));
      setQarzanAmount(Number(data.qarzanAmount));
      setInstallments(Number(data.installments));

      // setRepaymentDate(
      //     data.repaymentStartingDate
      //         ?.split("T")[0]
      // );

      setAgainstSalary(data.againstSalaryType);
      setPartialAmount(Number(data.partialAmount));
      setRemark(data.remark || "");

      // Gold Security
      setHasRehen(data?.providingGoldSecurity);
      if (data.goldSecurity) {
        setRehenGoldWeight(Number(data?.goldSecurity?.goldWeightGrams || 0));
        setRehenGoldKarat(String(data.goldSecurity.goldKarat));
        setRehenEstimatedValue(Number(data?.goldSecurity?.estimatedValue || 0));
        setRehenLocation(data?.goldSecurity?.whereIsItKept || "");
        setRehenCustodian(data?.goldSecurity?.withWhom || "");
        setRehenCustodianContact(data.goldSecurity?.custodianContact || "");
        setRehenDescription(data?.goldSecurity?.description || "");
      }

      // Guarantors
      setGuarantors(
        data.guarantors?.map((item: GuarantorApiResponse) => ({
          guarantorName: item.guarantorName || "",
          guarantorItsNumber: item.itsNumber || "",
          guarantorRelation: item.relationToApplicant || "",
          guarantorPhone: item.phoneNumber || "",
          guarantorEmail: item.emailAddress || "",
          guarantorAddress: item.address || "",
        })) || [],
      );

      const mappedDocuments: QarzanDocumentUpload[] =
        docResult
          ?.filter(
            (doc: QarzanApiDocument) => doc.moduleCode === "QARZAN_APPLICATION",
          )
          ?.map((doc: QarzanApiDocument) => {
            const latestVersion = doc?.versions?.[0];
            const allHistories =
              doc.versions?.flatMap(
                (version: DocumentHistoryNew) => version.histories || [],
              ) || [];
            return {
              id: String(doc.documentId ?? ""),
              documentMasterId: String(doc.documentMasterId ?? ""),
              documentId: String(doc.documentId ?? "jugnu"),
              name: doc.documentName ?? "",
              status: doc.status ?? "",
              isRequired: Boolean(doc.isMandatory),
              versions: doc.versions,
              uploadedUrl: latestVersion?.filePath ?? "",
              history: allHistories,
              file: null,
              acceptedFormats: doc.acceptedFormats,
              maxFileSizeKB: doc.maxFileSizeKB,
            };
          }) ?? [];
      setDocuments((prevDocuments) => {
        return prevDocuments.map((masterDoc) => {
          const uploadedDoc = mappedDocuments.find(
            (uploaded) =>
              String(uploaded.documentMasterId) === String(masterDoc.id),
          );

          if (!uploadedDoc) {
            return masterDoc;
          }

          return {
            ...masterDoc,
            documentId: uploadedDoc.documentId || "",
            uploadedUrl: uploadedDoc.uploadedUrl,
            status: uploadedDoc.status,
            versions: uploadedDoc.versions,
            history: uploadedDoc.history,
            acceptedFormats: uploadedDoc.acceptedFormats,
            maxFileSizeKB: uploadedDoc.maxFileSizeKB,
          };
        });
      });
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch application details");
    } finally {
      setLoading(false);
    }
  }, [id]);


  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        await getQarzanTypes();
        const docs = await fetchDocuments("qarzan_application");
        setDocuments(docs);
        if (id) {
          await getQarzanDetails(docs);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    loadInitialData();
  }, [id, getQarzanTypes, fetchDocuments, getQarzanDetails, refetchApi]);


  // Calculate monthly installment breakdown
  const calculateInstallments = () => {
    if (!qarzanAmount || !installments || installments === 0) return [];

    const monthlyAmount = qarzanAmount / installments;
    const installmentList = [];
    let currentDate = repaymentStartDate
      ? new Date(repaymentStartDate)
      : new Date();

    for (let i = 1; i <= installments; i++) {
      const isLastInstallment = i === installments;
      let amount = monthlyAmount;

      // Adjust last installment to ensure total matches exactly
      if (isLastInstallment) {
        const totalSoFar = monthlyAmount * (installments - 1);
        amount = qarzanAmount - totalSoFar;
        // Round to nearest whole number
        amount = Math.round(amount);
      } else {
        amount = Math.round(amount);
      }

      installmentList.push({
        month: i,
        date: new Date(currentDate),
        amount: amount,
      });

      // Move to next month
      currentDate = new Date(currentDate);
      currentDate.setMonth(currentDate.getMonth() + 1);
    }

    return installmentList;
  };

  const installmentBreakdown = calculateInstallments();

  // DOCUEMENT SECTION START
  const validateFile = (doc: QarzanDocumentUpload, file: File): boolean => {
    /* ------------------ FORMAT VALIDATION ------------------ */
    if (doc.acceptedFormatsList?.length) {
      const fileExtension = file.name.split(".").pop()?.toLowerCase();

      const allowedFormats = doc.acceptedFormatsList.map((f: string) =>
        f.trim().toLowerCase(),
      );

      if (!fileExtension || !allowedFormats.includes(fileExtension)) {
        toast.error(
          `Accepted format: ${allowedFormats.join(", ").toUpperCase()} only`,
        );
        return false;
      }
    }
    /* ------------------ SIZE VALIDATION ------------------ */
    if (doc.maxFileSizeKB) {
      const maxSizeBytes = Number(doc.maxFileSizeKB) * 1024;
      const tolerance = 5 * 1024;

      if (file.size > maxSizeBytes + tolerance) {
        toast.error(
          `File size must be less than or equal to ${formatFileSizeFromKB(
            doc.maxFileSizeKB,
          )}`,
        );
        return false;
      }
    }

    return true;
  };


  const handleDeleteDocument = (docId: string) => {
    setDocumentToDelete(docId);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteDocument = async () => {
    if (!deleteRemark.trim()) {
      toast.error("Please provide a reason for document deletion");
      return;
    }
    if (!documentToDelete) return;
    try {
      setIsUploading(true);
      const result = await rejectTourOperatorDocument(
        Number(documentToDelete),
        deleteRemark,
      );
      // update UI after success
      setDocuments((prev) =>
        prev.map((doc) =>
          doc.id === documentToDelete
            ? { ...doc, uploadedUrl: undefined, file: null }
            : doc,
        ),
      );
      toast.success(result?.response?.data ?? "Document rejected successfully");
      setRefetchApi(!refetchApi);
      setIsDeleteDialogOpen(false);
      setDocumentToDelete(null);
      setDeleteRemark("");
    } catch (error: unknown) {
      let errorMessage = "Failed to reject document";
      if (axios.isAxiosError(error)) {
        if (typeof error.response?.data === "string") {
          errorMessage = error.response.data;
        } else {
          errorMessage = error.message;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  const handleNewConfirmUpload = async () => {
    if (!rejectedDoc || reUploadFiles.length === 0) {
      toast.error("Please select a file");
      return;
    }

    const file = reUploadFiles[0];
    /* ------------------ File Size Validation ------------------ */
    const maxSizeMB = Number(rejectedDoc?.maxFileSizeKB || 0) / 1024;
    const fileSizeMB = file.size / (1024 * 1024);

    if (fileSizeMB > maxSizeMB + 0.01) {
      toast.error(`File size must be less than or equal to ${maxSizeMB} MB`);
      return;
    }

    /* ------------------ Accepted Format Validation ------------------ */
    if (rejectedDoc?.acceptedFormats?.trim()) {
      const fileExtension = file.name.split(".").pop()?.toLowerCase();

      const allowedFormats = rejectedDoc.acceptedFormats
        .split(",")
        .map((f) => f.trim().toLowerCase())
        .filter(Boolean);

      if (!fileExtension || !allowedFormats.includes(fileExtension)) {
        toast.error(
          `Accepted format: ${allowedFormats.join(", ")} Only (Max ${formatFileSizeFromKB(
            rejectedDoc.maxFileSizeKB,
          )})`,
        );
        return;
      }
    }

    try {
      setIsUploading(true); // ✅ start loading
      const formData = new FormData();
      formData.append("QarzanPublicId", String(id));
      formData.append("DocumentMasterId", String(rejectedDoc?.id));
      formData.append("File", file);
      const result = await reUploadQarzanApplicantDocuments(formData);
      toast.success(result || "Document re-uploaded successfully.");
      setRefetchApi(!refetchApi);
      setShowUploadDialog(false);
      setRejectedDoc(null);
      setReuploadFiles([]);
    } catch (error) {
      let errorMessage = "Failed to re-upload document";
      if (axios.isAxiosError(error)) {
        errorMessage =
          typeof error.response?.data === "string"
            ? error.response.data
            : error.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    } finally {
      setIsUploading(false); // ✅ stop loading (very important)
    }
  };
  // DOCUEMENT SECTION END

  const handleSubmit = async (status: "Draft" | "Posted") => {
    const errors: QarzanFormErrors = {
      // date: "",
      qarzanType: "",
      qarzanAmount: "",
      installments: "",
      repaymentStartDate: "",
      againstSalary: "",
      partialAmount: "",
      rehenGoldWeight: "",
      rehenGoldKarat: "",
      rehenLocation: "",
      rehenCustodian: "",
      guarantors: [],
      document: "",
    };

    // if (!date) { errors.date = "Please select Date"; }
    // Qardan Type
    if (!qarzanType) {
      errors.qarzanType = "Please select Qardan Type";
    }
    // Qardan Amount
    if (qarzanAmount <= 0) {
      errors.qarzanAmount = "Qardan Amount must be greater than 0";
    }
    // Installments
    if (installments <= 0) {
      errors.installments = "Installments must be greater than 0";
    }
    // Repayment Date
    if (!repaymentStartDate) {
      errors.repaymentStartDate = "Please select Repayment Starting Date";
    }
    // Partial Salary
    if (againstSalary === "partial") {
      if (partialAmount <= 0) {
        errors.partialAmount = "Please enter valid partial repayment amount";
      } else if (partialAmount > qarzanAmount / installments) {
        errors.partialAmount =
          "Partial amount cannot exceed monthly installment amount";
      }
    }
    // Rehen Validation
    if (hasRehen) {
      if (!rehenGoldWeight || rehenGoldWeight <= 0) {
        errors.rehenGoldWeight = "Please enter valid gold weight";
      }
      if (!rehenGoldKarat) {
        errors.rehenGoldKarat = "Please select gold karat";
      }
      if (!rehenLocation) {
        errors.rehenLocation = "Please specify where the gold is kept";
      }
      if (!rehenCustodian) {
        errors.rehenCustodian = "Please specify who is holding the gold";
      }
    }

    //Multiple Guarantor Validation
    const guarantorErrors: GuarantorError[] = [];

    guarantors.forEach((guarantor, index) => {
      const currentError: GuarantorError = {};
      if (!guarantor.guarantorName.trim()) {
        currentError.guarantorName = "Guarantor name is required";
      }
      if (!guarantor.guarantorItsNumber.trim()) {
        currentError.guarantorItsNumber = "ITS Number is required";
      }
      if (!guarantor.guarantorPhone.trim()) {
        currentError.guarantorPhone = "Phone number is required";
      }
      if (!guarantor.guarantorRelation.trim()) {
        currentError.guarantorRelation = "Relation is required";
      }
      guarantorErrors[index] = currentError;
    });
    errors.guarantors = guarantorErrors;

    //Set Errors
    setFormErrors(errors);

    const hasGuarantorErrors = guarantorErrors.some(
      (error) => Object.keys(error).length > 0,
    );

    const hasNormalErrors = Object.values(errors).some((value) => {
      if (Array.isArray(value)) {
        return value.some((item) => Object.keys(item).length > 0);
      }
      return value !== "";
    });

    //DOCUMENT CHECK
    const missingDocs = documents.filter(
      (doc) => doc.isRequired && !doc.file && !doc.uploadedUrl,
    );

    if (missingDocs.length > 0) {
      errors.document = `Please upload all required documents`;
      return;
    }

    if (hasNormalErrors || hasGuarantorErrors) {
      return;
    }

    try {
      const formData = new FormData();

      // FIRST BLOCK
      formData.append("PublicId", String(id));
      formData.append("ApplicationDate", new Date(date).toISOString());
      formData.append("EmployeeId", String(employeeData?.employeeId || 0));
      formData.append("ItsNumber", employeeData?.itsNumber || "");
      // DepartmentIds array
      employeeData?.department
        ?.split(",")
        ?.map((id) => id.trim())
        ?.forEach((id, index) => { formData.append(`DepartmentIds[${index}]`, id) });
      formData.append("CurrencyId", String(employeeData?.salaryCurrency || 0));
      formData.append("QarzanTypeId", String(Number(qarzanType) || 0));
      formData.append("Status", status);

      // SECOND BLOCK
      formData.append("IsApproved", "false");
      formData.append("ApproverId", "0");
      formData.append("QarzanAmount", String(qarzanAmount || 0));
      formData.append("Installments", String(installments || 0));
      formData.append("RepaymentStartingDate", new Date(repaymentStartDate).toISOString());
      formData.append("AgainstSalaryType", againstSalary);
      formData.append("PartialAmount", String(partialAmount || 0));
      formData.append("Remark", remark || "");

      // GOLD SECURITY
      formData.append("ProvidingGoldSecurity", String(hasRehen));
      if (hasRehen) {
        formData.append("GoldSecurity.GoldWeightGrams", String(rehenGoldWeight || 0));
        formData.append("GoldSecurity.GoldKarat", String(rehenGoldKarat || 0));
        formData.append("GoldSecurity.EstimatedValue", String(rehenEstimatedValue || 0));
        formData.append("GoldSecurity.WhereIsItKept", rehenLocation || "");
        formData.append("GoldSecurity.WithWhom", rehenCustodian || "");
        formData.append("GoldSecurity.CustodianContact", rehenCustodianContact || "");
        formData.append("GoldSecurity.Description", rehenDescription || "");
      }

      // GUARANTORS
      formData.append("HasGuarantors", "true");
      guarantors.forEach((item, index) => {
        formData.append(`Guarantors[${index}].GuarantorName`, item.guarantorName || "");
        formData.append(`Guarantors[${index}].ItsNumber`, item.guarantorItsNumber || "");
        formData.append(`Guarantors[${index}].RelationToApplicant`, item.guarantorRelation || "");
        formData.append(`Guarantors[${index}].PhoneNumber`, item.guarantorPhone || "");
        formData.append(`Guarantors[${index}].EmailAddress`, item.guarantorEmail || "");
        formData.append(`Guarantors[${index}].Address`, item.guarantorAddress || "");
      });

      // DOCUMENTS
      documents.forEach((doc, index) => {
        if (!doc.file) return;
        formData.append(`Documents[${index}].DocumentMasterId`, String(doc.id));
        formData.append(`Documents[${index}].File`, doc.file);
        formData.append(`Documents[${index}].FileKey`, doc.file.name);
      });
      setLoading(true)
      await updateQarzanApplicant(formData, String(id));
      const statusText = status === "Draft" ? "saved as draft" : "posted";
      toast.success(`Application ${statusText} successfully!`);
      onNavigate("/qardan-application");
    } catch (error) {
      console.error(error);
      toast.error("Failed to submit qardan application");
    } finally {
      setLoading(false)
    }
  };

  const monthlyEMI =
    installments > 0 ? (qarzanAmount / installments).toFixed(2) : "0.00";
  // Set default repayment start date to next month
  useEffect(() => {
    if (!repaymentStartDate) {
      const nextMonth = new Date();
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      nextMonth.setDate(1); // Start on 1st of next month
      setRepaymentStartDate(nextMonth.toISOString().split("T")[0]);
    }
  }, [repaymentStartDate]);


  return (
    <div className="space-y-6">
      {loading && <FullScreenLoading />}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Details */}
          <Card className="bg-white/95 backdrop-blur-sm border-[#5B9BD5]/20 shadow-lg">
            <CardHeader className="border-b border-gray-100 pb-2">
              <CardTitle
                style={{ color: "#5B9BD5" }}
                className="flex items-center gap-2"
              >
                <HandCoins className="w-5 h-5" />
                Personal & Application Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <InputFieldset
                    label="Application Number"
                    disabled
                    // required
                    id="applicantName"
                    // placeholder='Auto generated'
                    value={applicationNo}
                    className="cursor-not-allowed"
                  />
                </div>

                <div>
                  <InputFieldset
                    disabled
                    type="date"
                    label="Date"
                    required
                    id="date"
                    value={date}
                    onChange={(e) => {
                      setDate(e.target.value);
                    }}
                    className="cursor-not-allowed"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 cursor-not-allowed">
                <div>
                  <InputFieldset
                    label="Employee Name"
                    // required
                    disabled
                    id="code"
                    value={employeeData.name}
                    className="cursor-not-allowed"
                  />
                </div>
                <div>
                  <InputFieldset
                    label="ITS Number"
                    // required
                    disabled
                    id="itsNumber"
                    value={employeeData.itsNumber}
                    className="cursor-not-allowed"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <InputFieldset
                    label="Department"
                    disabled
                    id="Department"
                    value={employeeData.departmentNames}
                    className="cursor-not-allowed"
                  />
                </div>
                <div>
                  <InputFieldset
                    label="Currency"
                    disabled
                    id="Currency"
                    value={employeeData.CurrencyCode}
                    className="cursor-not-allowed"
                  />
                </div>
              </div>

              <div>
                <SelectFieldset label="Qardan Type" required>
                  <Select
                    value={qarzanType}
                    onValueChange={(value) => {
                      setQarzanType(value);
                      // Clear error on change
                      if (formErrors.qarzanType) {
                        setFormErrors({ ...formErrors, qarzanType: "" });
                      }
                    }}
                  >
                    <SelectTriggerUI
                      className={formErrors.qarzanType ? "border-red-500" : ""}
                    >
                      <SelectValue placeholder="Select Qardan Type" />
                    </SelectTriggerUI>
                    <SelectContent>
                      {qarzanTypes.map((type, i) => (
                        <SelectItem
                          key={type.publicId || i}
                          value={String(type.id)}
                        >
                          {type.qarzanTypeName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </SelectFieldset>
                {formErrors.qarzanType && (
                  <p className="text-red-500 text-xs">
                    {formErrors.qarzanType}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Amount & Payment Details */}
          <Card className="bg-white/95 backdrop-blur-sm border-[#5B9BD5]/20 shadow-lg">
            <CardHeader className="border-b border-gray-100 pb-2">
              <CardTitle
                style={{ color: "#5B9BD5" }}
                className="flex items-center gap-2"
              >
                <DollarSign className="w-5 h-5" />
                Qardan Amount & Payment Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <InputFieldset
                    label="Qardan Amount"
                    type="text"
                    required
                    id="name"
                    value={qarzanAmount || ""}
                    min="0"
                    step="0.01"
                    onChange={(e) => {
                      const value = allowOnlyNumbers(e.target.value);
                      setQarzanAmount(parseFloat(value) || 0);
                      // Clear error on change
                      if (formErrors.qarzanAmount) {
                        setFormErrors({ ...formErrors, qarzanAmount: "" });
                      }
                    }}
                    placeholder="0.00"
                    className={formErrors.qarzanAmount ? "border-red-500" : ""}
                  />
                  {formErrors.qarzanAmount && (
                    <p className="text-red-500 text-xs">
                      {formErrors.qarzanAmount}
                    </p>
                  )}
                </div>
                <div>
                  <InputFieldset
                    label="Installments (Monthly) "
                    type="text"
                    required
                    value={installments || ""}
                    min="0"
                    placeholder="0"
                    onChange={(e) => {
                      const value = allowOnlyNumbers(e.target.value);
                      setInstallments(parseInt(value) || 0);
                      // Clear error on change
                      if (formErrors.installments) {
                        setFormErrors({ ...formErrors, installments: "" });
                      }
                    }}
                    className={formErrors.installments ? "border-red-500" : ""}
                  />
                  {formErrors.installments && (
                    <p className="text-red-500 text-xs">
                      {formErrors.installments}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <InputFieldset
                  label="Repayment Starting Date"
                  type="date"
                  required
                  value={repaymentStartDate}
                  onChange={(e) => {
                    setRepaymentStartDate(e.target.value);
                    // Clear error on change
                    if (formErrors.repaymentStartDate) {
                      setFormErrors({ ...formErrors, repaymentStartDate: "" });
                    }
                  }}
                  className={
                    formErrors.repaymentStartDate ? "border-red-500" : ""
                  }
                />
                {formErrors.repaymentStartDate && (
                  <p className="text-red-500 text-xs">
                    {formErrors.repaymentStartDate}
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  First installment will be deducted on this date
                </p>
              </div>

              <div>
                <SelectFieldset label="Against Salary" required>
                  <Select
                    value={againstSalary}
                    onValueChange={(value) => {
                      setAgainstSalary(value);
                      if (formErrors.againstSalary) {
                        setFormErrors({ ...formErrors, againstSalary: "" });
                      }
                      // Clear partial amount error if switching to complete
                      if (value === "complete" && formErrors.partialAmount) {
                        setFormErrors({ ...formErrors, partialAmount: "" });
                      }
                    }}
                  >
                    <SelectTriggerUI
                      className={
                        formErrors.againstSalary ? "border-red-500" : ""
                      }
                    >
                      <SelectValue placeholder="Select Against Salary" />
                    </SelectTriggerUI>
                    <SelectContent>
                      <SelectItem value="complete">
                        Complete - Full installment deducted from salary
                      </SelectItem>
                      <SelectItem value="partial">
                        Partial - Part deducted from salary
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </SelectFieldset>
                {formErrors.againstSalary && (
                  <p className="text-red-500 text-xs">
                    {formErrors.againstSalary}
                  </p>
                )}
              </div>

              {againstSalary === "partial" && (
                <div>
                  <InputFieldset
                    label="Partial Amount (Per Month)"
                    type="text"
                    required
                    value={partialAmount || ""}
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    onChange={(e) => {
                      const value = allowOnlyNumbers(e.target.value);
                      setPartialAmount(parseFloat(value) || 0);
                      // Clear error on change
                      if (formErrors.partialAmount) {
                        setFormErrors({ ...formErrors, partialAmount: "" });
                      }
                    }}
                    className={formErrors.partialAmount ? "border-red-500" : ""}
                  />
                  {formErrors.partialAmount && (
                    <p className="text-red-500 text-xs">
                      {formErrors.partialAmount}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Amount to be deducted from salary monthly (remaining to be
                    paid separately)
                  </p>
                </div>
              )}

              {qarzanAmount > 0 && installments > 0 && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="space-y-2">
                    <p className="text-sm text-blue-900">
                      <strong>Monthly Installment:</strong>{" "}
                      {employeeData?.CurrencyCode} {monthlyEMI}
                    </p>
                    {againstSalary === "partial" && partialAmount > 0 && (
                      <>
                        <p className="text-sm text-blue-900">
                          <strong>Deducted from Salary:</strong>{" "}
                          {employeeData?.CurrencyCode}{" "}
                          {partialAmount.toFixed(2)}
                        </p>
                        <p className="text-sm text-blue-900">
                          <strong>To be paid separately:</strong>{" "}
                          {employeeData?.CurrencyCode}{" "}
                          {(parseFloat(monthlyEMI) - partialAmount).toFixed(2)}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              )}

              <div>
                <SelectFieldset label="Remark">
                  <TextareaUI
                    id="Remark"
                    placeholder="Additional notes or comments"
                    value={remark}
                    onChange={(e) => setRemark(e.target.value)}
                    className="border-gray-300 min-h-[100px]"
                    rows={4}
                  />
                </SelectFieldset>
              </div>
            </CardContent>
          </Card>

          {/* Rehen (Gold Security) Details */}
          <Card className="bg-white/95 backdrop-blur-sm border-[#5B9BD5]/20 shadow-lg">
            <CardHeader className="border-b border-gray-100 pb-2">
              <div className="flex items-center justify-between">
                <CardTitle
                  style={{ color: "#5B9BD5" }}
                  className="flex items-center gap-2"
                >
                  <Shield className="w-5 h-5" />
                  Rehen Details
                </CardTitle>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={hasRehen}
                    onChange={(e) => setHasRehen(e.target.checked)}
                    className="w-4 h-4 text-[#5B9BD5] border-gray-300 rounded focus:ring-[#5B9BD5]"
                  />
                  <span className="text-sm text-gray-600">
                    Providing Gold Security
                  </span>
                </label>
              </div>
            </CardHeader>
            {hasRehen && (
              <CardContent className="space-y-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <InputFieldset
                      required
                      type="number"
                      label="Gold Weight (grams)"
                      id="goldweight"
                      value={rehenGoldWeight || ""}
                      min="0"
                      step="0.01"
                      onChange={(e) => {
                        const value = allowOnlyNumbers(e.target.value);
                        setRehenGoldWeight(parseFloat(value) || 0);
                        // Clear error on change
                        if (formErrors.rehenGoldWeight) {
                          setFormErrors({ ...formErrors, rehenGoldWeight: "" });
                        }
                      }}
                      placeholder="0.00"
                      className={
                        formErrors.rehenGoldWeight ? "border-red-500" : ""
                      }
                    />
                    {formErrors.rehenGoldWeight && (
                      <p className="text-red-500 text-xs">
                        {formErrors.rehenGoldWeight}
                      </p>
                    )}
                  </div>
                  <div>
                    <SelectFieldset label="Gold Karat" required>
                      <Select
                        value={rehenGoldKarat}
                        onValueChange={(value) => {
                          setRehenGoldKarat(value);
                          if (formErrors.rehenGoldKarat) {
                            setFormErrors({
                              ...formErrors,
                              rehenGoldKarat: "",
                            });
                          }
                        }}
                      >
                        <SelectTriggerUI
                          className={
                            formErrors.rehenGoldKarat ? "border-red-500" : ""
                          }
                        >
                          <SelectValue placeholder="Select Qardan Type" />
                        </SelectTriggerUI>
                        <SelectContent>
                          <SelectItem value="24">
                            24 Karat (99.9% pure)
                          </SelectItem>
                          <SelectItem value="22">
                            22 Karat (91.6% pure)
                          </SelectItem>
                          <SelectItem value="21">
                            21 Karat (87.5% pure)
                          </SelectItem>
                          <SelectItem value="18">
                            18 Karat (75% pure)
                          </SelectItem>
                          <SelectItem value="14">
                            14 Karat (58.3% pure)
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </SelectFieldset>
                    {formErrors.rehenGoldKarat && (
                      <p className="text-red-500 text-xs">
                        {formErrors.rehenGoldKarat}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <InputFieldset
                    type="text"
                    label={`Estimated Value (${employeeData?.CurrencyCode})`}
                    id="estimatedvalue"
                    value={rehenEstimatedValue || ""}
                    min="0"
                    step="0.01"
                    onChange={(e) => {
                      const value = allowOnlyNumbers(e.target.value);
                      setRehenEstimatedValue(parseFloat(value) || 0);
                    }}
                    placeholder="0.00"
                    className={"border-red-500"}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <InputFieldset
                      required
                      type="text"
                      label={"Where is it kept?"}
                      id="whereit"
                      value={rehenLocation || ""}
                      onChange={(e) => {
                        setRehenLocation(removeSpecialCharacters(e.target.value));
                        if (formErrors.rehenLocation) {
                          setFormErrors({ ...formErrors, rehenLocation: "" });
                        }
                      }}
                      placeholder="e.g., Office Safe, Bank Locker"
                      className={"border-red-500"}
                    />
                    {formErrors.rehenLocation && (
                      <p className="text-red-500 text-xs">
                        {formErrors.rehenLocation}
                      </p>
                    )}
                  </div>

                  <div>
                    <InputFieldset
                      required
                      type="text"
                      label="With Whom?"
                      id="whereit"
                      value={rehenCustodian || ""}
                      onChange={(e) => {
                        setRehenCustodian(removeSpecialCharacters(e.target.value));
                        if (formErrors.rehenCustodian) {
                          setFormErrors({ ...formErrors, rehenCustodian: "" });
                        }
                      }}
                      placeholder="e.g., HR Manager, Finance Officer"
                      className={"border-red-500"}
                    />
                    {formErrors.rehenCustodian && (
                      <p className="text-red-500 text-xs">
                        {formErrors.rehenCustodian}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <InputFieldset
                    type="text"
                    label="Custodian Contact"
                    id="custodiancontact"
                    value={rehenCustodianContact || ""}
                    onChange={(e) => {
                      const value = validatePhoneNumberInput(e.target.value);
                      setRehenCustodianContact(value);
                    }}
                    placeholder="Phone number or email"
                    className={"border-red-500"}
                  />
                </div>

                <div>
                  <SelectFieldset label="Description / Additional Details">
                    <TextareaUI
                      id="rehenDescription"
                      value={rehenDescription}
                      onChange={(e) => setRehenDescription(e.target.value)}
                      placeholder="Describe the jewelry items, condition, etc."
                      className="border-gray-300 min-h-[80px]"
                      rows={4}
                    />
                  </SelectFieldset>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Guarantor Details */}
          <Card className="bg-white/95 backdrop-blur-sm border-[#5B9BD5]/20 shadow-lg">
            <CardHeader className="border-b border-gray-100 pb-2">
              <div className="flex items-center justify-between">
                <CardTitle
                  style={{ color: "#5B9BD5" }}
                  className="flex items-center gap-2"
                >
                  <UserPlus className="w-5 h-5" />
                  Guarantor Details
                </CardTitle>
                <Button
                  type="button"
                  onClick={addGuarantor}
                  className="flex items-center gap-2"
                >
                  <UserPlus className="w-4 h-4" />
                  Add Guarantor
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="space-y-2">
                {guarantors.map((guarantor, index) => (
                  <Card key={index} className="p-4 border">
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="font-semibold">Guarantor {index + 1}</h3>

                      {guarantors.length > 1 && (
                        <Button
                          type="button"
                          variant="destructive"
                          onClick={() => removeGuarantor(index)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <InputFieldset
                          required
                          type="text"
                          label="Guarantor Name"
                          id="guarantorName"
                          value={guarantor.guarantorName || ""}
                          onChange={(e) => {
                            const value = removeSpecialCharacters(e.target.value);
                            handleGuarantorChange(index, 'guarantorName', value)
                          }}
                          placeholder="Full name"
                          className={"border-red-500"}
                        />
                        {formErrors?.guarantors?.[index]?.guarantorName && (
                          <p className="text-red-500 text-sm mt-1">
                            {formErrors.guarantors[index].guarantorName}
                          </p>
                        )}
                      </div>

                      <div>
                        <InputFieldset
                          required
                          type="text"
                          label="ITS Number "
                          id="guarantorItsNumber"
                          value={guarantor.guarantorItsNumber || ""}
                          onChange={(e) => {
                            const value = removeSpecialCharacters(e.target.value);
                            handleGuarantorChange(index, 'guarantorItsNumber', value)
                          }}
                          placeholder="8-digit ITS number"
                          className={"border-red-500"}
                        />
                        {formErrors?.guarantors?.[index]
                          ?.guarantorItsNumber && (
                            <p className="text-red-500 text-sm mt-1">
                              {formErrors.guarantors[index].guarantorItsNumber}
                            </p>
                          )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <InputFieldset
                          required
                          type="text"
                          label="Relation to Applicant"
                          id="guarantorRelation"
                          value={guarantor.guarantorRelation || ""}
                          onChange={(e) =>
                            handleGuarantorChange(
                              index,
                              "guarantorRelation",
                              e.target.value,
                            )
                          }
                          placeholder="e.g., Brother, Friend, Colleague"
                          className={"border-red-500"}
                        />
                        {formErrors?.guarantors?.[index]?.guarantorRelation && (
                          <p className="text-red-500 text-sm mt-1">
                            {formErrors.guarantors[index].guarantorRelation}
                          </p>
                        )}
                      </div>

                      <div>
                        <InputFieldset
                          required
                          type="text"
                          label=" Phone Number"
                          id="phoneNumber"
                          value={guarantor.guarantorPhone || ""}
                          onChange={(e) => {
                            const value = validatePhoneNumberInput(e.target.value);
                            handleGuarantorChange(index, 'guarantorPhone', value)
                          }}
                          placeholder="+92 300 1234567"
                          className={"border-red-500"}
                        />
                        {formErrors?.guarantors?.[index]?.guarantorPhone && (
                          <p className="text-red-500 text-sm mt-1">
                            {formErrors.guarantors[index].guarantorPhone}
                          </p>
                        )}
                      </div>
                    </div>

                    <div>
                      <InputFieldset
                        type="email"
                        label="Email Address"
                        id="guarantorEmail"
                        value={guarantor.guarantorEmail || ""}
                        onChange={(e) =>
                          handleGuarantorChange(
                            index,
                            "guarantorEmail",
                            e.target.value,
                          )
                        }
                        placeholder="guarantor@example.com"
                        className={"border-red-500"}
                      />
                    </div>

                    <div>
                      <SelectFieldset label="Address">
                        <TextareaUI
                          id="Address"
                          value={guarantor.guarantorAddress || ""}
                          onChange={(e) =>
                            handleGuarantorChange(
                              index,
                              "guarantorAddress",
                              e.target.value,
                            )
                          }
                          placeholder="Complete residential address"
                          className="border-gray-300 min-h-[80px]"
                          rows={4}
                        />
                      </SelectFieldset>
                    </div>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Document Upload */}
          <Card className="border-none bg-white/95 backdrop-blur-sm shadow-2xl rounded-2xl sm:rounded-3xl">
            <CardHeader>
              <CardTitle className="text-gray-900">
                Upload Supporting Documents
                <span className="text-destructive">*</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {formErrors.document && (
                <Alert
                  variant="destructive"
                  className="mb-6 border-destructive/50 bg-destructive/10 border-red-400"
                >
                  <AlertCircle className="w-4 h-4" />
                  <AlertDescription className="text-destructive text-sm">
                    {formErrors.document}
                  </AlertDescription>
                </Alert>
              )}

              <div className="border border-gray-200 rounded-lg overflow-hidden">
                {/* HEADING */}
                <div
                  style={{ backgroundColor: "#5B9BD5" }}
                  className="grid grid-cols-12 text-white px-4 py-3"
                >
                  <div className="col-span-1 font-medium">No.</div>
                  <div className="col-span-5 font-medium">Document Name</div>
                  <div className="col-span-6 font-medium">Upload File</div>
                </div>

                <div className="space-y-4">
                  {documents.map((doc, index) => (
                    <div
                      key={doc.id}
                      className="border border-gray-200 rounded-lg overflow-hidden"
                    >
                      <div className="bg-white hover:bg-gray-50 transition-colors p-4">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
                          <span className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-gray-900 flex-shrink-0">
                            {index + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-gray-900">
                              {doc.name || doc?.file?.name || doc.documentName}
                              {doc.isRequired ||
                                (doc?.isMandatory && (
                                  <span className="text-red-500 ml-1">*</span>
                                ))}
                            </p>
                            <p className="text-xs text-red-400">
                              {`Accepted format: ${doc.acceptedFormats} (Max ${formatFileSizeFromKB(
                                doc.maxFileSizeKB,
                              )})`}
                            </p>
                          </div>
                          <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto">

                            {/* DOCUMENT HISTORY TOGGLE */}
                            {doc.history && doc.history.length > 0 && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => toggleDocHistory(doc.id)}
                                className="text-sm border-gray-300 hover:bg-gray-50 rounded-xl flex items-center gap-1"
                              >
                                <History className="w-3 h-3" />
                                History ({doc.history.length})
                                {expandedDocHistory[doc.id] ? (
                                  <ChevronUp className="w-3 h-3" />
                                ) : (
                                  <ChevronDown className="w-3 h-3" />
                                )}
                              </Button>
                            )}
                            {/* DOCUMENT HISTORY TOGGLE */}

                            {doc.uploadedUrl || doc.file ? (
                              <>
                                <span className="flex gap-4 text-sm max-w-[180px] truncate">
                                  {doc.status !== "REJECTED" &&
                                    doc.uploadedUrl && (
                                      <>
                                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                                        {doc.file
                                          ? doc.file.name
                                          : "Document Uploaded"}
                                      </>
                                    )}
                                  {doc.file ? doc?.file?.name : ""}
                                </span>
                              </>
                            ) : (
                              <span className="text-sm text-gray-500">
                                Not uploaded
                              </span>
                            )}

                            {doc.status === "REJECTED" ? (
                              <>
                                <CircleX className="w-5 h-5 text-red-600" />
                                <span className="text-red-500 text-sm">
                                  Document Rejected
                                </span>
                                <Button
                                  size="sm"
                                  type="button"
                                  style={{
                                    backgroundColor: "#5B9BD5",
                                  }}
                                  className="text-white text-xs"
                                  onClick={() => {
                                    setRejectedDoc(doc);
                                    setShowUploadDialog(true);
                                  }}
                                >
                                  <Upload className="w-3.5 h-3.5 mr-1" />
                                  Reupload
                                </Button>
                              </>
                            ) : doc.uploadedUrl ? (
                              // DOCUMENT EXISTS
                              <>
                                <button
                                  className="hover:bg-gray-100 rounded-lg p-1.5 transition-colors"
                                  style={{ color: "#5B9BD5" }}
                                >
                                  <Eye
                                    className="w-4 h-4 cursor-pointer"
                                    onClick={() => {
                                      const fullUrl = getFileViewUrl(
                                        doc.uploadedUrl,
                                      );
                                      window.open(fullUrl, "_blank");
                                    }}
                                  />
                                </button>

                                {/* {canDelete && ( */}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() =>
                                    handleDeleteDocument(String(doc.documentId))
                                  }
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-xl"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                                {/* )} */}
                              </>
                            ) : (
                              // NEW DOCUMENT
                              <>
                                <input
                                  type="file"
                                  id={`upload-${doc.id}`}
                                  className="hidden"
                                  accept={
                                    doc.acceptedFormatsList?.length
                                      ? doc.acceptedFormatsList
                                        .map((f: string) => `.${f.trim()}`)
                                        .join(",")
                                      : "*"
                                  }
                                  onChange={(e) => {
                                    const file = e.target.files?.[0] || null;
                                    if (!file) return;
                                    const isValid = validateFile(doc, file);
                                    if (!isValid) {
                                      e.target.value = "";
                                      return;
                                    }
                                    // STORE IN STATE
                                    setDocuments((prev) =>
                                      prev.map((item) =>
                                        item.id === doc.id
                                          ? { ...item, file }
                                          : item,
                                      ),
                                    );
                                  }}
                                />

                                <Button
                                  size="sm"
                                  type="button"
                                  onClick={() =>
                                    document
                                      .getElementById(`upload-${doc.id}`)
                                      ?.click()
                                  }
                                  style={{
                                    backgroundColor: "#5B9BD5",
                                  }}
                                  className="text-white text-xs"
                                >
                                  <Upload className="w-3.5 h-3.5 mr-1" />
                                  Upload
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {doc.versions &&
                        doc.versions.length > 0 &&
                        expandedDocHistory[doc.id] && (
                          <div className="bg-gradient-to-r from-blue-50 to-white border-t border-gray-200 p-4">
                            <h4
                              className="text-sm mb-3"
                              style={{ color: "#5B9BD5" }}
                            >
                              📋 Document Upload History ({doc.versions.length}{" "}
                              versions)
                            </h4>

                            <div className="space-y-4">
                              {doc.versions.map((version, versionIndex) => (
                                <div key={versionIndex}>
                                  {version.histories?.map((hist, histIndex) => (
                                    <div
                                      key={hist.id || histIndex}
                                      className={`p-3 rounded-lg border mb-2 ${hist.historyStatus?.toLowerCase() ===
                                        "approved"
                                        ? "bg-green-50 border-green-200"
                                        : hist.historyStatus?.toLowerCase() ===
                                          "rejected"
                                          ? "bg-red-50 border-red-200"
                                          : "bg-gray-50 border-gray-200"
                                        }`}
                                    >
                                      <div className="flex items-start justify-between gap-3">
                                        <div className="flex-1">
                                          <div className="flex items-center gap-2 mb-1">
                                            <Badge
                                              className={`text-xs ${hist.historyStatus?.toLowerCase() ===
                                                "approved"
                                                ? "bg-green-100 text-green-700 border-green-300"
                                                : hist.historyStatus?.toLowerCase() ===
                                                  "rejected"
                                                  ? "bg-red-100 text-red-700 border-red-300"
                                                  : "bg-gray-100 text-gray-700 border-gray-300"
                                                }`}
                                            >
                                              {hist.historyStatus
                                                ? hist.historyStatus.toUpperCase()
                                                : "PENDING"}
                                            </Badge>

                                            {hist.actionAt && (
                                              <span className="text-xs text-gray-600">
                                                {
                                                  formatUploadedDateTime(
                                                    hist.actionAt,
                                                  ).date
                                                }{" "}
                                                at{" "}
                                                {
                                                  formatUploadedDateTime(
                                                    hist.actionAt,
                                                  ).time
                                                }
                                              </span>
                                            )}
                                          </div>

                                          {hist.remarks && (
                                            <p className="text-xs text-gray-700 mt-1">
                                              <span className="text-gray-900">
                                                Remarks:
                                              </span>{" "}
                                              {hist.remarks}
                                            </p>
                                          )}
                                        </div>

                                        <button
                                          className="hover:bg-gray-100 rounded-lg p-1 transition-colors flex-shrink-0"
                                          style={{ color: "#5B9BD5" }}
                                          onClick={() => {
                                            if (!version.filePath) return;
                                            const fullUrl = getFileViewUrl(
                                              version.filePath,
                                            );
                                            window.open(fullUrl, "_blank");
                                          }}
                                        >
                                          <Eye className="w-4 h-4 cursor-pointer" />
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Summary Sidebar */}
        <div className="lg:col-span-1">
          <div className="sticky top-6 space-y-4">
            <Card className="bg-gradient-to-br from-[#5B9BD5] to-[#4A8BC2] text-white shadow-lg">
              <CardHeader className="pb-3">
                <CardTitle className="text-white">
                  Application Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center pb-3 border-b border-white/20 text-white/80">
                  <span className="">Application No</span>
                  <span className="font-mono">{applicationNo}</span>
                </div>
                <div className="text-white/80 flex justify-between items-center pb-3 border-b border-white/20">
                  <span className="">Employee</span>
                  <span className="text-right">{employeeData?.name}</span>
                </div>
                <div className="text-white/80 flex justify-between items-center pb-3 border-b border-white/20">
                  <span className="">ITS Number</span>
                  <span className="">{employeeData?.itsNumber}</span>
                </div>
                <div className="text-white/80 flex justify-between items-center pb-3 border-b border-white/20">
                  <span className="">Qardan Amount</span>
                  <span className="text-lg">
                    {employeeData?.CurrencyCode}{" "}
                    {qarzanAmount.toLocaleString()}
                  </span>
                </div>
                <div className="text-white/80 flex justify-between items-center pb-3 border-b border-white/20">
                  <span className="">Installments</span>
                  <span className="text-lg">{installments}</span>
                </div>
                {qarzanAmount > 0 && installments > 0 && (
                  <div className="text-white/80 flex justify-between items-center pb-3 border-b border-white/20">
                    <span className="">Monthly EMI</span>
                    <span className="text-lg">
                      {employeeData?.CurrencyCode} {monthlyEMI}
                    </span>
                  </div>
                )}
                <div className="text-white/80 flex justify-between items-center pb-3 border-b border-white/20">
                  <span className="">Against Salary</span>
                  <span className="capitalize">{againstSalary}</span>
                </div>
                {againstSalary === "partial" && partialAmount > 0 && (
                  <div className="text-white/80 flex justify-between items-center pb-3 border-b border-white/20">
                    <span className="">Salary Deduction</span>
                    <span className="">
                      {employeeData?.CurrencyCode} {partialAmount.toFixed(2)}
                    </span>
                  </div>
                )}
                {repaymentStartDate && (
                  <div className="text-white/80 flex justify-between items-center pb-3 border-b border-white/20">
                    <span className="">Start Date</span>
                    <span className="">
                      {new Date(repaymentStartDate).toLocaleDateString(
                        "en-GB",
                        {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        },
                      )}
                    </span>
                  </div>
                )}
                <div className="pt-2">
                  <p className="text-xs text-white/70">
                    This is an interest-free loan (Qardan Hasana) to be repaid
                    in {installments} monthly installments.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Installment Breakdown in Sidebar */}
            {installmentBreakdown.length > 0 && (
              <Card className="bg-white/95 backdrop-blur-sm border-[#5B9BD5]/20 shadow-lg">
                <CardHeader className="border-b border-gray-100 pb-4">
                  <CardTitle
                    style={{ color: "#5B9BD5" }}
                    className="flex items-center gap-2"
                  >
                    <Calendar className="w-5 h-5" />
                    Installment Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent className="">
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {installmentBreakdown.map((installment, index) => (
                      <div
                        key={index}
                        className={`flex justify-between items-center p-3 rounded-lg border ${installment.month === installmentBreakdown.length
                          ? "bg-green-50 border-green-200"
                          : "bg-gray-50 border-gray-200"
                          }`}
                      >
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            Installment {installment.month}
                          </p>
                          <p className="text-xs text-gray-500">
                            {installment.date.toLocaleDateString("en-GB", {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            })}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-gray-900">
                            {employeeData?.CurrencyCode}{" "}
                            {installment.amount.toLocaleString()}
                          </p>
                          {installment.month ===
                            installmentBreakdown.length && (
                              <p className="text-xs text-green-600 font-medium">
                                Final Installment
                              </p>
                            )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                      <p className="font-bold text-gray-900">Total Amount:</p>
                      <p
                        className="text-xl font-bold"
                        style={{ color: "#5B9BD5" }}
                      >
                        {employeeData?.CurrencyCode}{" "}
                        {installmentBreakdown
                          .reduce((sum, inst) => sum + inst.amount, 0)
                          .toLocaleString()}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <Button
              onClick={() => handleSubmit("Posted")}
              className="w-full text-white shadow-lg hover:opacity-90 h-12"
              style={{ backgroundColor: "#5B9BD5" }}
            >
              <Save className="w-4 h-4 mr-2" />
              Post Application
            </Button>

            <Button
              onClick={() => handleSubmit("Draft")}
              variant="outline"
              className="w-full border-[#5B9BD5] text-[#5B9BD5] hover:bg-[#5B9BD5]/10 h-12"
            >
              <FileText className="w-4 h-4 mr-2" />
              Save as Draft
            </Button>

            <Button
              variant="outline"
              onClick={() => onNavigate("qardan-application")}
              className="w-full border-gray-300"
            >
              Cancel
            </Button>
          </div>
        </div>

        {/*RE-UPLOAD NEW DOCUMENT START POPUP*/}
        {showUploadDialog && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="bg-white shadow-lg rounded-2xl w-[500px]">
              <CardContent className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Upload Documents
                </h2>
                <div className="space-y-4">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                    <Upload className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                    <Input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      className="hidden"
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        setReuploadFiles(files);
                      }}
                    // onChange={(e) => {
                    //   const file = e.target.files?.[0] || null;
                    //   if (!file || !rejectedDoc) {
                    //     return;
                    //   }
                    //   // FILE EXTENSION
                    //   const fileExtension = file.name.split(".").pop()?.toLowerCase() || "";
                    //   // ALLOWED EXTENSIONS
                    //   const allowedFormats = rejectedDoc.acceptedFormatsList?.map((format: string) => format.toLowerCase()) || [];

                    //   // VALIDATE FORMAT
                    //   if (!allowedFormats.includes(fileExtension)) {
                    //     toast.error(
                    //       `Only ${allowedFormats.join(
                    //         ", ",
                    //       )} files are allowed`,
                    //     );
                    //     e.target.value = "";
                    //     return;
                    //   }

                    //   // VALIDATE SIZE
                    //   const maxSizeBytes = Number(rejectedDoc.maxFileSizeKB || 0) * 1024;
                    //   if (file.size > maxSizeBytes) {
                    //     toast.error(
                    //       `Maximum file size is ${formatFileSizeFromKB(
                    //         rejectedDoc.maxFileSizeKB,
                    //       )}`,
                    //     );
                    //     e.target.value = "";
                    //     return;
                    //   }
                    //   setReuploadFiles([file]);
                    // }}
                    />
                    <p className="text-sm text-gray-600 mt-2 mb-2">
                      {reUploadFiles.length > 0
                        ? `${reUploadFiles.length} file(s) selected`
                        : "No files selected"}
                    </p>
                    <div className="flex justify-center">
                      <Label htmlFor="batch-upload">
                        <Button
                          variant="outline"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          Select Files
                        </Button>
                      </Label>
                    </div>
                  </div>
                  <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-3">
                    <p className="text-xs text-amber-800">Upload documents</p>
                  </div>
                </div>
                <div className="flex items-center justify-end gap-3 mt-6">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowUploadDialog(false);
                      setRejectedDoc(null);
                      setReuploadFiles([]);
                    }}
                    className="text-gray-900 hover:bg-gray-100"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleNewConfirmUpload}
                    disabled={isUploading}
                    style={{ backgroundColor: "#5B9BD5" }}
                    className="text-white hover:opacity-90 px-6 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isUploading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Confirm Upload
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        {/* UPLOAD NEW DOCUMENT END */}

        {/* Delete Document Confirmation Dialog */}
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle style={{ color: "#5B9BD5" }}>
                Remove Document
              </DialogTitle>
              <DialogDescription>
                Please provide a reason for removing this document. The
                applicant will be notified to upload a revised document.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="deleteRemark" className="text-gray-900">
                  Reason for Document Revision{" "}
                  <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="deleteRemark"
                  value={deleteRemark}
                  onChange={(e) => setDeleteRemark(e.target.value)}
                  placeholder="e.g., Document is not clear, incorrect document uploaded, signature missing..."
                  className="min-h-[120px] bg-white border-gray-300 rounded-xl resize-none"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsDeleteDialogOpen(false);
                  setDocumentToDelete(null);
                  setDeleteRemark("");
                }}
                className="rounded-xl"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmDeleteDocument}
                disabled={isUploading}
                style={{ backgroundColor: "#5B9BD5" }}
                className="text-white rounded-xl"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    loading...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Remove Document
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};
